import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Plus, Trash2, ArrowRightLeft, Building2, ArrowDownLeft, ArrowUpRight, Filter, BookOpen, User, FileText, Zap, Wallet, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils';

const BankManagement = ({ user }) => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [balances, setBalances] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transactionDialog, setTransactionDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [accountFilter, setAccountFilter] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Payment data for new transaction types
  const [pendingVendorBills, setPendingVendorBills] = useState([]);
  const [pendingSalaries, setPendingSalaries] = useState([]);
  const [employeesForAdvance, setEmployeesForAdvance] = useState([]);
  const [pendingUtilityBills, setPendingUtilityBills] = useState([]);
  const [openingLiabilities, setOpeningLiabilities] = useState([]);
  const [loadingPaymentData, setLoadingPaymentData] = useState(false);
  
  // Selected items for payment
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedBills, setSelectedBills] = useState({});
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedUtilityBills, setSelectedUtilityBills] = useState({});
  const [selectedLiability, setSelectedLiability] = useState(null);
  const [liabilityPayAmount, setLiabilityPayAmount] = useState('');
  const [liabilityTypeFilter, setLiabilityTypeFilter] = useState('all');
  
  const [transactionForm, setTransactionForm] = useState({
    date: new Date().toISOString().split('T')[0],
    bank_account: '',
    transaction_type: '',
    amount: '',
    reference: '',
    transfer_to: '',
    expense_group: '',
    expense_category: ''
  });

  const canManageBank = user?.role === 'admin' || user?.role === 'accountant';
  const canDeleteTransactions = user?.role === 'admin';

  // Bank accounts fetched from backend
  const [bankAccounts, setBankAccounts] = useState([]);

  const fetchBankAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bank-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankAccounts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch bank accounts');
      // Fallback to default accounts if API fails
      setBankAccounts(["592", "OD", "Z-Del", "Z-Dine", "S-Del", "S-Dine", "Easy-Din", "G-Rabit", "Loan"]);
    }
  };

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bank-transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch transactions');
      setLoading(false);
    }
  };

  const fetchBalances = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bank-balances`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBalances(response.data);
    } catch (error) {
      console.error('Failed to fetch balances');
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/expense-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.filter(c => c.is_active));
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  // Fetch payment data based on transaction type
  const fetchPaymentData = async (type) => {
    setLoadingPaymentData(true);
    const token = localStorage.getItem('token');
    
    try {
      if (type === 'pay_bills') {
        const response = await axios.get(`${API}/bank-pending-vendor-bills`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingVendorBills(response.data || []);
      } else if (type === 'pay_salary') {
        const response = await axios.get(`${API}/bank-pending-salaries`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingSalaries(response.data || []);
      } else if (type === 'pay_advance') {
        const response = await axios.get(`${API}/bank-employees-for-advance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEmployeesForAdvance(response.data || []);
      } else if (type === 'pay_utility') {
        const response = await axios.get(`${API}/bank-pending-utility-bills`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingUtilityBills(response.data || []);
      } else if (type === 'pay_liability') {
        const response = await axios.get(`${API}/opening-liabilities/payable/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOpeningLiabilities(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch payment data:', error);
      toast.error('Failed to load payment data');
    } finally {
      setLoadingPaymentData(false);
    }
  };

  useEffect(() => {
    fetchBankAccounts();
    fetchTransactions();
    fetchBalances();
    fetchCategories();
  }, []);

  // Fetch payment data when transaction type changes
  useEffect(() => {
    if (['pay_bills', 'pay_salary', 'pay_advance', 'pay_utility', 'pay_liability'].includes(transactionForm.transaction_type)) {
      fetchPaymentData(transactionForm.transaction_type);
    }
  }, [transactionForm.transaction_type]);

  // Groups to exclude from bank expense transactions
  const excludedGroups = [
    'Bank Deposits', 'NC', 'DISCOUNT', 'Payment Modes', 'SERVICE CHARGE', 'STAFF FOOD BILLS',
    'Discounts', 'NC Category', 'Online Payments', 'Service Charge Less', 'Vendor Payments'
  ];

  const excludedManagementCategories = [
    'rajan sir food bill', 
    'gagan sir food bill', 
    'manjot sir food bill',
    'rajan sir food',
    'manjot sir food',
    'gagan sir food'
  ];

  const filteredGroups = [...new Set(categories.map(c => c.group))]
    .filter(g => !excludedGroups.some(eg => eg.toLowerCase() === g.toLowerCase()));

  const filteredCategories = categories.filter(c => {
    // Match by group (case-insensitive)
    if (c.group?.toLowerCase() !== transactionForm.expense_group?.toLowerCase()) return false;
    
    // Filter out food bills from Management group
    if (transactionForm.expense_group?.toLowerCase() === 'management') {
      const categoryNameLower = c.name.toLowerCase();
      // Check if category name contains "food bill" for any of the sirs
      if (categoryNameLower.includes('food bill') && 
          (categoryNameLower.includes('rajan') || 
           categoryNameLower.includes('manjot') || 
           categoryNameLower.includes('gagan'))) {
        return false;
      }
    }
    return true;
  });

  // Calculate total for selected vendor bills
  const getSelectedBillsTotal = () => {
    return Object.entries(selectedBills)
      .filter(([, data]) => data.selected)
      .reduce((sum, [, data]) => sum + (parseFloat(data.amount) || 0), 0);
  };

  // Calculate total for selected utility bills
  const getSelectedUtilityTotal = () => {
    return Object.entries(selectedUtilityBills)
      .filter(([, data]) => data.selected)
      .reduce((sum, [, data]) => sum + (parseFloat(data.amount) || 0), 0);
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    if (submitting) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      // Handle different payment types
      if (transactionForm.transaction_type === 'pay_bills') {
        // Pay selected vendor bills
        const payments = Object.entries(selectedBills)
          .filter(([, data]) => data.selected && data.amount > 0)
          .map(([billId, data]) => ({
            bill_id: billId,
            amount: parseFloat(data.amount),
            bank_account: transactionForm.bank_account,
            date: transactionForm.date,
            remarks: transactionForm.reference
          }));
        
        if (payments.length === 0) {
          toast.error('Please select at least one bill to pay');
          setSubmitting(false);
          return;
        }
        
        for (const payment of payments) {
          await axios.post(`${API}/bank-pay-vendor-bill`, payment, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        toast.success(`Paid ${payments.length} vendor bill(s) successfully!`);
        
      } else if (transactionForm.transaction_type === 'pay_salary') {
        if (!selectedSalary) {
          toast.error('Please select a salary to pay');
          setSubmitting(false);
          return;
        }
        await axios.post(`${API}/bank-pay-salary`, {
          salary_id: selectedSalary.id,
          amount: parseFloat(transactionForm.amount),
          bank_account: transactionForm.bank_account,
          date: transactionForm.date,
          remarks: transactionForm.reference
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Paid salary to ${selectedSalary.employee_name}`);
        
      } else if (transactionForm.transaction_type === 'pay_advance') {
        if (!selectedEmployee) {
          toast.error('Please select an employee');
          setSubmitting(false);
          return;
        }
        await axios.post(`${API}/bank-pay-advance-salary`, {
          employee_id: selectedEmployee.id,
          amount: parseFloat(transactionForm.amount),
          bank_account: transactionForm.bank_account,
          date: transactionForm.date,
          remarks: transactionForm.reference
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Paid advance to ${selectedEmployee.name}`);
        
      } else if (transactionForm.transaction_type === 'pay_utility') {
        const payments = Object.entries(selectedUtilityBills)
          .filter(([, data]) => data.selected && data.amount > 0)
          .map(([billId, data]) => ({
            bill_id: billId,
            amount: parseFloat(data.amount),
            bank_account: transactionForm.bank_account,
            date: transactionForm.date,
            remarks: transactionForm.reference
          }));
        
        if (payments.length === 0) {
          toast.error('Please select at least one utility bill to pay');
          setSubmitting(false);
          return;
        }
        
        for (const payment of payments) {
          await axios.post(`${API}/bank-pay-utility-bill`, payment, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        toast.success(`Paid ${payments.length} utility bill(s) successfully!`);
        
      } else if (transactionForm.transaction_type === 'pay_liability') {
        // Pay opening liability via bank
        if (!selectedLiability) {
          toast.error('Please select a liability to pay');
          setSubmitting(false);
          return;
        }
        
        const payAmount = parseFloat(liabilityPayAmount);
        if (!payAmount || payAmount <= 0) {
          toast.error('Please enter a valid payment amount');
          setSubmitting(false);
          return;
        }
        
        // Backend expects Form data, not JSON
        const formData = new FormData();
        formData.append('liability_id', selectedLiability.id);
        formData.append('amount', payAmount.toString());
        formData.append('bank_account', transactionForm.bank_account);
        formData.append('date', transactionForm.date);
        formData.append('remarks', transactionForm.reference || `Opening Liability Payment - ${selectedLiability.entity_name}`);
        
        await axios.post(`${API}/bank-pay-liability`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success(`Paid ₹${payAmount.toLocaleString()} to ${selectedLiability.entity_name}`);
        
      } else {
        // Regular transaction types (debit, transfer, cash_withdrawal)
        let reference = transactionForm.reference;
        if (transactionForm.transaction_type === 'debit' && transactionForm.expense_category) {
          reference = `[${transactionForm.expense_group}/${transactionForm.expense_category}] ${transactionForm.reference || ''}`.trim();
        }
        
        await axios.post(`${API}/bank-transactions`, {
          date: transactionForm.date,
          bank_account: transactionForm.bank_account,
          transaction_type: transactionForm.transaction_type,
          amount: parseFloat(transactionForm.amount),
          reference: reference,
          transfer_to: transactionForm.transfer_to || null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const msg = transactionForm.transaction_type === 'transfer' 
          ? 'Transfer completed!' 
          : 'Transaction added!';
        toast.success(msg);
      }
      
      setTransactionDialog(false);
      resetForm();
      fetchTransactions();
      fetchBalances();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to process transaction'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setTransactionForm({
      date: new Date().toISOString().split('T')[0],
      bank_account: '',
      transaction_type: '',
      amount: '',
      reference: '',
      transfer_to: '',
      expense_group: '',
      expense_category: ''
    });
    setSelectedVendor('');
    setSelectedBills({});
    setSelectedSalary(null);
    setSelectedEmployee(null);
    setSelectedUtilityBills({});
    setSelectedLiability(null);
    setLiabilityPayAmount('');
    setLiabilityTypeFilter('all');
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (deleting) return;
    
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/bank-transactions/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const reversedItems = response.data?.reversed_items || {};
      let message = 'Transaction deleted';
      
      if (Object.keys(reversedItems).length > 0) {
        const details = Object.entries(reversedItems)
          .map(([type, info]) => `${type}: ₹${info.amount}`)
          .join(', ');
        message = `Transaction deleted. Reversed: ${details}`;
      }
      
      toast.success(message);
      setDeleteDialog(false);
      setTransactionToDelete(null);
      fetchTransactions();
      fetchBalances();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete transaction'));
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (transaction) => {
    setTransactionToDelete(transaction);
    setDeleteDialog(true);
  };

  const getTransactionBadge = (type) => {
    switch (type) {
      case 'credit':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><ArrowDownLeft className="w-3 h-3 mr-1" />Credit</Badge>;
      case 'debit':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><ArrowUpRight className="w-3 h-3 mr-1" />Debit</Badge>;
      case 'transfer_out':
        return <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100"><ArrowRightLeft className="w-3 h-3 mr-1" />Transfer Out</Badge>;
      case 'transfer_in':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100"><ArrowRightLeft className="w-3 h-3 mr-1" />Transfer In</Badge>;
      case 'cash_withdrawal':
        return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100"><ArrowUpRight className="w-3 h-3 mr-1" />Cash W/D</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  const filteredTransactions = accountFilter === 'all' 
    ? transactions 
    : transactions.filter(t => t.bank_account === accountFilter);

  const totalBalance = Object.values(balances).reduce((sum, b) => sum + (b?.balance || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64">Loading...</div>;

  // Get selected vendor's bills for display
  const selectedVendorData = pendingVendorBills.find(v => v.vendor_id === selectedVendor);

  return (
    <div className="space-y-6 p-6" data-testid="bank-management">
      {/* Bank Balances Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Bank Account Balances
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bank Account</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-right w-24">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bankAccounts.map(account => (
                <TableRow 
                  key={account} 
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => navigate(`/banks/ledger/${encodeURIComponent(account)}`)}
                >
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      {account}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-bold">
                    ₹{(balances[account]?.balance || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/banks/ledger/${encodeURIComponent(account)}`);
                      }}
                    >
                      <ExternalLink className="w-4 h-4 text-blue-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-blue-50 font-bold">
                <TableCell className="text-blue-800">Total Balance</TableCell>
                <TableCell className="text-right text-blue-800 text-lg">
                  ₹{totalBalance.toLocaleString()}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Main Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Bank Transactions
          </CardTitle>
          <div className="flex gap-2">
            <Select value={accountFilter} onValueChange={setAccountFilter}>
              <SelectTrigger className="w-[150px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Banks</SelectItem>
                {bankAccounts.map(account => (
                  <SelectItem key={account} value={account}>{account}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canManageBank && (
              <Button onClick={() => setTransactionDialog(true)} data-testid="add-transaction-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Transaction
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Bank</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                {canDeleteTransactions && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                    No transactions found
                  </TableCell>
                </TableRow>
              ) : filteredTransactions.map(trans => (
                <TableRow key={trans.id}>
                  <TableCell>{trans.date}</TableCell>
                  <TableCell><Badge variant="outline">{trans.bank_account}</Badge></TableCell>
                  <TableCell>{getTransactionBadge(trans.transaction_type)}</TableCell>
                  <TableCell className="max-w-xs truncate">{trans.reference}</TableCell>
                  <TableCell className={`text-right font-medium ${trans.transaction_type === 'credit' || trans.transaction_type === 'transfer_in' ? 'text-green-600' : 'text-red-600'}`}>
                    {trans.transaction_type === 'credit' || trans.transaction_type === 'transfer_in' ? '+' : '-'}₹{trans.amount?.toLocaleString()}
                  </TableCell>
                  {canDeleteTransactions && (
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(trans)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={deleting}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Transaction Dialog */}
      <Dialog open={transactionDialog} onOpenChange={setTransactionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Bank Transaction</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddTransaction} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={transactionForm.date}
                  onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Bank Account</Label>
                <Select
                  value={transactionForm.bank_account}
                  onValueChange={(value) => setTransactionForm({ ...transactionForm, bank_account: value })}
                >
                  <SelectTrigger data-testid="transaction-account-select">
                    <SelectValue placeholder="Select Bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map(account => (
                      <SelectItem key={account} value={account}>{account}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Transaction Type</Label>
              <Select
                value={transactionForm.transaction_type}
                onValueChange={(value) => {
                  setTransactionForm({ 
                    ...transactionForm, 
                    transaction_type: value,
                    amount: '',
                    expense_group: '',
                    expense_category: ''
                  });
                  setSelectedVendor('');
                  setSelectedBills({});
                  setSelectedSalary(null);
                  setSelectedEmployee(null);
                  setSelectedUtilityBills({});
                }}
              >
                <SelectTrigger data-testid="transaction-type-select">
                  <SelectValue placeholder="Select Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pay_bills">
                    <span className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      Pay Vendor Bills
                    </span>
                  </SelectItem>
                  <SelectItem value="pay_salary">
                    <span className="flex items-center gap-2">
                      <User className="w-4 h-4 text-green-600" />
                      Pay Salary
                    </span>
                  </SelectItem>
                  <SelectItem value="pay_advance">
                    <span className="flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-purple-600" />
                      Pay Advance Salary
                    </span>
                  </SelectItem>
                  <SelectItem value="pay_utility">
                    <span className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-yellow-600" />
                      Pay Utility Bills
                    </span>
                  </SelectItem>
                  <SelectItem value="pay_liability">
                    <span className="flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-orange-600" />
                      Pay Opening Liabilities
                    </span>
                  </SelectItem>
                  <SelectItem value="debit">
                    <span className="flex items-center gap-2">
                      <ArrowUpRight className="w-4 h-4 text-red-600" />
                      Other Expense (Money Out)
                    </span>
                  </SelectItem>
                  <SelectItem value="cash_withdrawal">
                    <span className="flex items-center gap-2">
                      <ArrowUpRight className="w-4 h-4 text-purple-600" />
                      Cash Withdrawal (To Cashier)
                    </span>
                  </SelectItem>
                  <SelectItem value="transfer">
                    <span className="flex items-center gap-2">
                      <ArrowRightLeft className="w-4 h-4 text-blue-600" />
                      Transfer Between Banks
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Pay Vendor Bills */}
            {transactionForm.transaction_type === 'pay_bills' && (
              <div className="space-y-4 border rounded-lg p-4 bg-blue-50">
                <div className="space-y-2">
                  <Label>Select Vendor</Label>
                  <Select value={selectedVendor} onValueChange={(v) => { setSelectedVendor(v); setSelectedBills({}); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {pendingVendorBills.map(v => (
                        <SelectItem key={v.vendor_id} value={v.vendor_id}>
                          {v.vendor_name} (₹{v.total_pending.toLocaleString()} pending)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedVendorData && (
                  <div className="space-y-2">
                    <Label>Select Bills to Pay</Label>
                    <div className="max-h-48 overflow-y-auto border rounded bg-white p-2">
                      {selectedVendorData.bills.map(bill => (
                        <div key={bill.id} className="flex items-center gap-3 py-2 border-b last:border-0">
                          <Checkbox
                            checked={selectedBills[bill.id]?.selected || false}
                            onCheckedChange={(checked) => {
                              setSelectedBills(prev => ({
                                ...prev,
                                [bill.id]: { selected: checked, amount: checked ? bill.balance : 0 }
                              }));
                            }}
                          />
                          <div className="flex-1">
                            <span className="font-medium">#{bill.bill_number}</span>
                            <span className="text-gray-500 text-sm ml-2">{bill.bill_date}</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            Balance: ₹{bill.balance.toLocaleString()}
                          </div>
                          {selectedBills[bill.id]?.selected && (
                            <Input
                              type="number"
                              className="w-28"
                              value={selectedBills[bill.id]?.amount || ''}
                              onChange={(e) => {
                                const amt = Math.min(parseFloat(e.target.value) || 0, bill.balance);
                                setSelectedBills(prev => ({
                                  ...prev,
                                  [bill.id]: { ...prev[bill.id], amount: amt }
                                }));
                              }}
                              max={bill.balance}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="text-right font-medium text-blue-600">
                      Total: ₹{getSelectedBillsTotal().toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pay Salary */}
            {transactionForm.transaction_type === 'pay_salary' && (
              <div className="space-y-4 border rounded-lg p-4 bg-green-50">
                <div className="space-y-2">
                  <Label>Select Salary to Pay</Label>
                  <Select 
                    value={selectedSalary?.id || ''} 
                    onValueChange={(v) => {
                      const sal = pendingSalaries.find(s => s.id === v);
                      setSelectedSalary(sal);
                      if (sal) {
                        setTransactionForm(prev => ({ ...prev, amount: sal.balance }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Employee & Month" />
                    </SelectTrigger>
                    <SelectContent>
                      {pendingSalaries.map(sal => (
                        <SelectItem key={sal.id} value={sal.id}>
                          {sal.employee_name} - {sal.month} (₹{sal.balance?.toLocaleString()} pending)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedSalary && (
                  <div className="space-y-2">
                    <Label>Amount to Pay</Label>
                    <Input
                      type="number"
                      value={transactionForm.amount}
                      onChange={(e) => setTransactionForm({ ...transactionForm, amount: Math.min(parseFloat(e.target.value) || 0, selectedSalary.balance) })}
                      max={selectedSalary.balance}
                      required
                    />
                    <div className="text-sm text-gray-500">Max: ₹{selectedSalary.balance?.toLocaleString()}</div>
                  </div>
                )}
              </div>
            )}

            {/* Pay Advance Salary */}
            {transactionForm.transaction_type === 'pay_advance' && (
              <div className="space-y-4 border rounded-lg p-4 bg-purple-50">
                <div className="space-y-2">
                  <Label>Select Employee</Label>
                  <Select 
                    value={selectedEmployee?.id || ''} 
                    onValueChange={(v) => {
                      const emp = employeesForAdvance.find(e => e.id === v);
                      setSelectedEmployee(emp);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employeesForAdvance.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name} ({emp.department}) - Current Advance: ₹{emp.advance_balance?.toLocaleString() || 0}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedEmployee && (
                  <div className="space-y-2">
                    <Label>Advance Amount</Label>
                    <Input
                      type="number"
                      value={transactionForm.amount}
                      onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                      placeholder="Enter advance amount"
                      required
                    />
                  </div>
                )}
              </div>
            )}

            {/* Pay Utility Bills */}
            {transactionForm.transaction_type === 'pay_utility' && (
              <div className="space-y-4 border rounded-lg p-4 bg-yellow-50">
                <Label>Select Utility Bills to Pay</Label>
                <div className="max-h-48 overflow-y-auto border rounded bg-white p-2">
                  {pendingUtilityBills.length === 0 ? (
                    <div className="text-center text-gray-500 py-4">No pending utility bills</div>
                  ) : pendingUtilityBills.map(bill => (
                    <div key={bill.id} className="flex items-center gap-3 py-2 border-b last:border-0">
                      <Checkbox
                        checked={selectedUtilityBills[bill.id]?.selected || false}
                        onCheckedChange={(checked) => {
                          setSelectedUtilityBills(prev => ({
                            ...prev,
                            [bill.id]: { selected: checked, amount: checked ? bill.balance : 0 }
                          }));
                        }}
                      />
                      <div className="flex-1">
                        <span className="font-medium">{bill.bill_type_label}</span>
                        <span className="text-gray-500 text-sm ml-2">{bill.bill_month}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Balance: ₹{bill.balance?.toLocaleString()}
                      </div>
                      {selectedUtilityBills[bill.id]?.selected && (
                        <Input
                          type="number"
                          className="w-28"
                          value={selectedUtilityBills[bill.id]?.amount || ''}
                          onChange={(e) => {
                            const amt = Math.min(parseFloat(e.target.value) || 0, bill.balance);
                            setSelectedUtilityBills(prev => ({
                              ...prev,
                              [bill.id]: { ...prev[bill.id], amount: amt }
                            }));
                          }}
                          max={bill.balance}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div className="text-right font-medium text-yellow-700">
                  Total: ₹{getSelectedUtilityTotal().toLocaleString()}
                </div>
              </div>
            )}

            {/* Pay Opening Liabilities */}
            {transactionForm.transaction_type === 'pay_liability' && (
              <div className="space-y-4 border rounded-lg p-4 bg-orange-50">
                {/* Liability Type Filter */}
                <div className="space-y-2">
                  <Label>Liability Type</Label>
                  <Select 
                    value={liabilityTypeFilter} 
                    onValueChange={(v) => {
                      setLiabilityTypeFilter(v);
                      setSelectedLiability(null);
                      setLiabilityPayAmount('');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="vendor">Vendor</SelectItem>
                      <SelectItem value="employee">Salary</SelectItem>
                      <SelectItem value="utility">Utility</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Liability Selection */}
                <div className="space-y-2">
                  <Label>Select Liability</Label>
                  <Select 
                    value={selectedLiability?.id || ''} 
                    onValueChange={(v) => {
                      const liability = openingLiabilities.find(l => l.id === v);
                      setSelectedLiability(liability);
                      setLiabilityPayAmount(liability?.balance?.toString() || '');
                    }}
                    disabled={loadingPaymentData}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingPaymentData ? "Loading..." : "Select Liability"} />
                    </SelectTrigger>
                    <SelectContent>
                      {openingLiabilities
                        .filter(l => liabilityTypeFilter === 'all' || l.liability_type === liabilityTypeFilter)
                        .length === 0 ? (
                        <SelectItem value="none" disabled>No pending {liabilityTypeFilter !== 'all' ? liabilityTypeFilter : ''} liabilities</SelectItem>
                      ) : openingLiabilities
                        .filter(l => liabilityTypeFilter === 'all' || l.liability_type === liabilityTypeFilter)
                        .map(liability => (
                        <SelectItem key={liability.id} value={liability.id}>
                          <span className="flex items-center justify-between gap-4">
                            <span>{liability.entity_name || liability.vendor_name || 'Unknown'}</span>
                            <span className="text-orange-600">₹{liability.balance?.toLocaleString()}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedLiability && (
                  <div className="space-y-3 p-3 bg-white rounded border">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Liability Type:</span>
                      <span className="font-medium capitalize">{selectedLiability.liability_type === 'employee' ? 'Salary' : selectedLiability.liability_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Amount:</span>
                      <span className="font-medium">₹{selectedLiability.amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pending Balance:</span>
                      <span className="font-medium text-orange-600">₹{selectedLiability.balance?.toLocaleString()}</span>
                    </div>
                    {selectedLiability.remarks && (
                      <div className="text-sm text-gray-500 border-t pt-2">
                        {selectedLiability.remarks}
                      </div>
                    )}
                    <div className="space-y-2 border-t pt-3">
                      <Label>Payment Amount</Label>
                      <Input
                        type="number"
                        value={liabilityPayAmount}
                        onChange={(e) => {
                          const amt = Math.min(parseFloat(e.target.value) || 0, selectedLiability.balance);
                          setLiabilityPayAmount(amt.toString());
                        }}
                        max={selectedLiability.balance}
                        placeholder="Enter amount to pay"
                      />
                      <div className="flex gap-2">
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => setLiabilityPayAmount(selectedLiability.balance.toString())}
                        >
                          Pay Full
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => setLiabilityPayAmount((selectedLiability.balance / 2).toString())}
                        >
                          Pay Half
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Other Expense (Debit) */}
            {transactionForm.transaction_type === 'debit' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Expense Group</Label>
                    <Select
                      value={transactionForm.expense_group}
                      onValueChange={(value) => setTransactionForm({ ...transactionForm, expense_group: value, expense_category: '' })}
                    >
                      <SelectTrigger data-testid="expense-group-select">
                        <SelectValue placeholder="Select Group" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredGroups.map(group => (
                          <SelectItem key={group} value={group}>{group}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {transactionForm.expense_group && (
                    <div className="space-y-2">
                      <Label>Expense Category</Label>
                      <Select
                        value={transactionForm.expense_category}
                        onValueChange={(value) => setTransactionForm({ ...transactionForm, expense_category: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Category" />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredCategories.map(cat => (
                            <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    value={transactionForm.amount}
                    onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                    placeholder="Enter amount"
                    required
                  />
                </div>
              </>
            )}

            {/* Cash Withdrawal */}
            {transactionForm.transaction_type === 'cash_withdrawal' && (
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  type="number"
                  value={transactionForm.amount}
                  onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                  placeholder="Enter cash withdrawal amount"
                  required
                />
              </div>
            )}

            {/* Transfer */}
            {transactionForm.transaction_type === 'transfer' && (
              <>
                <div className="space-y-2">
                  <Label>Transfer To</Label>
                  <Select
                    value={transactionForm.transfer_to}
                    onValueChange={(value) => setTransactionForm({ ...transactionForm, transfer_to: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Destination Bank" />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.filter(a => a !== transactionForm.bank_account).map(account => (
                        <SelectItem key={account} value={account}>{account}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    value={transactionForm.amount}
                    onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                    placeholder="Enter transfer amount"
                    required
                  />
                </div>
              </>
            )}

            {/* Reference/Remarks */}
            <div className="space-y-2">
              <Label>Reference / Remarks</Label>
              <Input
                value={transactionForm.reference}
                onChange={(e) => setTransactionForm({ ...transactionForm, reference: e.target.value })}
                placeholder="Enter reference or remarks"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setTransactionDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || !transactionForm.bank_account || !transactionForm.transaction_type}
              >
                {submitting ? 'Processing...' : 'Submit'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Transaction</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to delete this transaction?</p>
            {transactionToDelete && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p><strong>Date:</strong> {transactionToDelete.date}</p>
                <p><strong>Bank:</strong> {transactionToDelete.bank_account}</p>
                <p><strong>Amount:</strong> ₹{transactionToDelete.amount?.toLocaleString()}</p>
                <p><strong>Reference:</strong> {transactionToDelete.reference}</p>
              </div>
            )}
            <p className="mt-4 text-orange-600 text-sm">
              ⚠️ If this transaction was a payment (vendor bill, salary, utility, opening liability), deleting it will reverse the payment in the respective ledger.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteTransaction(transactionToDelete?.id)}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankManagement;
